import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  CircleDot,
  Clock,
  FlaskConical,
  Lock,
  QrCode,
  RotateCcw,
  Rocket,
  UserPlus,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { ApiError, api, get } from '../../lib/api'
import {
  formatDuration,
  formatMoment,
  fromLocalInput,
  secondsUntil,
  toLocalInput,
  useServerNow,
} from '../../lib/countdown'
import { cn } from '../../lib/cn'
import type { AdminSettings, AppMode, SettingsDateField } from './types'
import { AButton, Chip, Note, Panel, Section } from './Section'

/**
 * The editable settings, in the shape the inputs use.
 *
 * The four date fields are keyed by their server names so the save body is a
 * straight conversion rather than a mapping nobody can check.
 */
type Draft = { mode: AppMode } & Record<SettingsDateField, string>

const draftFrom = (data: AdminSettings): Draft => ({
  mode: data.settings.mode,
  registrationOpensAt: toLocalInput(data.settings.registrationOpensAt),
  registrationClosesAt: toLocalInput(data.settings.registrationClosesAt),
  boothOpensAt: toLocalInput(data.settings.boothOpensAt),
  boothClosesAt: toLocalInput(data.settings.boothClosesAt),
})

const MODES = [
  {
    value: 'development' as const,
    label: 'Development',
    icon: FlaskConical,
    blurb:
      'Testing and the dry run. Both windows below are ignored — students can sign up and booths can scan whatever the dates say.',
  },
  {
    value: 'production' as const,
    label: 'Production',
    icon: Rocket,
    blurb:
      'Event day. Both windows are enforced: a countdown before each one opens, a closed notice after it ends.',
  },
]

// ── The two windows, described once ───────────────────────────

/**
 * Everything that differs between the registration window and the booth
 * window. Declared rather than written twice, because the editor, the preview
 * and the validation are identical for both — only the wording is not, and
 * wording duplicated across two panels is wording that drifts.
 */
const WINDOWS = [
  {
    key: 'registration' as const,
    title: 'Registration window',
    description:
      'When students may sign up. Sign-in is blocked before it opens, but not after it closes. Production mode only.',
    icon: UserPlus,
    opensField: 'registrationOpensAt' as const,
    closesField: 'registrationClosesAt' as const,
    opensHint: 'Before this, students can neither register nor sign in — they see a countdown.',
    closesHint: 'After this, new sign-ups stop. Signing in keeps working.',
    audience: 'On the student registration page',
    beforeDetail: (left: string) =>
      `Students see a countdown and cannot sign in either. Registration opens in ${left}.`,
    closedDetail:
      'New sign-ups are refused. Registered students can still sign in and use their pass.',
    openDetail: (left: string | null) =>
      left
        ? `Students see the form. It closes in ${left}.`
        : 'Students see the form. No closing time is set, so it stays open.',
  },
  {
    key: 'booth' as const,
    title: 'Booth scanning window',
    description: 'When booth managers may mark attendance. Applied in Production mode only.',
    icon: QrCode,
    opensField: 'boothOpensAt' as const,
    closesField: 'boothClosesAt' as const,
    opensHint: 'Before this, managers see a countdown instead of the scan buttons.',
    closesHint: 'After this, managers see “Booth visits are closed”.',
    audience: 'On the booth scanner',
    beforeDetail: (left: string) =>
      `Managers see a countdown and cannot scan. Scanning opens in ${left}.`,
    closedDetail:
      'Managers see “Booth visits are closed”. No attendance can be marked at any booth.',
    openDetail: (left: string | null) =>
      left
        ? `Managers can scan. Scanning closes in ${left}.`
        : 'Managers can scan. No closing time is set, so it stays open.',
  },
]

type WindowSpec = (typeof WINDOWS)[number]

/**
 * The server's status words, in the console's vocabulary.
 *
 * 'before' is what the API calls it; "counting down" is what the panel calls
 * it. Mapped rather than compared raw, so the "saved vs typed" hint below can
 * tell a genuine drift from a difference in wording.
 */
const LIVE_LABEL: Record<'before' | 'open' | 'closed', string> = {
  before: 'counting down',
  open: 'open',
  closed: 'closed',
}

/**
 * How one window will behave, worked out in the browser so the preview follows
 * what Admin is typing rather than what was last saved.
 *
 * Deliberately a copy of the server's rule in `lib/settings.ts` and not the
 * source of it — nothing a browser computes decides whether a registration or
 * a scan is accepted. If the two ever disagree, the server wins; that is the
 * right way round.
 */
function previewOf(spec: WindowSpec, draft: Draft, nowMs: number) {
  if (draft.mode !== 'production') {
    return {
      tone: 'info' as const,
      status: 'Always open',
      detail: 'Development mode — this window is not applied.',
    }
  }

  const opens = fromLocalInput(draft[spec.opensField])
  const closes = fromLocalInput(draft[spec.closesField])
  const untilOpen = secondsUntil(opens, nowMs)
  const untilClose = secondsUntil(closes, nowMs)

  if (opens && untilOpen !== null && untilOpen > 0) {
    return {
      tone: 'attention' as const,
      status: 'Counting down',
      detail: spec.beforeDetail(formatDuration(untilOpen)),
    }
  }
  if (closes && untilClose === 0) {
    return { tone: 'critical' as const, status: 'Closed', detail: spec.closedDetail }
  }
  return {
    tone: 'positive' as const,
    status: 'Open',
    detail: spec.openDetail(closes ? formatDuration(untilClose ?? 0) : null),
  }
}

/** True when a window would close before it opened — refused on the server too. */
function isBackwards(spec: WindowSpec, draft: Draft) {
  const opens = draft[spec.opensField]
  const closes = draft[spec.closesField]
  return !!opens && !!closes && new Date(closes).getTime() <= new Date(opens).getTime()
}

// ── One window's editor ───────────────────────────────────────

function WindowPanel({
  spec,
  draft,
  nowMs,
  liveStatus,
  onChange,
  children,
}: {
  spec: WindowSpec
  draft: Draft
  nowMs: number
  /** What the *saved* window is doing, as the server reports it. */
  liveStatus: string
  onChange: (field: SettingsDateField, value: string) => void
  children?: React.ReactNode
}) {
  const preview = previewOf(spec, draft, nowMs)
  const backwards = isBackwards(spec, draft)
  const Icon = spec.icon

  const fields = [
    { field: spec.opensField, label: 'Opens', hint: spec.opensHint },
    { field: spec.closesField, label: 'Closes', hint: spec.closesHint },
  ]

  return (
    <Panel
      title={spec.title}
      description={spec.description}
      actions={
        <Chip
          tone={
            preview.tone === 'positive'
              ? 'positive'
              : preview.tone === 'critical'
                ? 'critical'
                : preview.tone === 'attention'
                  ? 'attention'
                  : 'info'
          }
        >
          <Icon className="size-3" strokeWidth={2.6} />
          {preview.status}
        </Chip>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map(({ field, label, hint }) => (
          <div key={field} className="min-w-0">
            <label
              htmlFor={field}
              className="mb-1 block text-[12.5px] font-bold text-[#0B1E3F]"
            >
              {label}
            </label>
            <div className="flex gap-2">
              <input
                id={field}
                type="datetime-local"
                value={draft[field]}
                onChange={(e) => onChange(field, e.target.value)}
                className="h-11 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              {draft[field] && (
                <AButton
                  tone="outline"
                  size="sm"
                  className="h-11 shrink-0"
                  onClick={() => onChange(field, '')}
                >
                  Clear
                </AButton>
              )}
            </div>
            <p className="mt-1 px-1 text-[11.5px] text-slate-400">
              {draft[field] ? hint : 'Not set — no limit on this side.'}
            </p>
          </div>
        ))}
      </div>

      {backwards && (
        <Note tone="error" icon={AlertTriangle} className="mt-3">
          The closing time must be after the opening time.
        </Note>
      )}

      {/* What the window as *typed* will do, right now. */}
      <Note
        tone={
          preview.tone === 'critical'
            ? 'error'
            : preview.tone === 'attention'
              ? 'attention'
              : preview.tone === 'positive'
                ? 'green'
                : 'neutral'
        }
        icon={Clock}
        className="mt-3"
      >
        <p className="font-semibold">
          {spec.audience}: {preview.status}
        </p>
        <p className="mt-0.5 text-[13px]">{preview.detail}</p>
        {draft.mode === 'production' && (draft[spec.opensField] || draft[spec.closesField]) && (
          <p className="mt-1.5 text-xs opacity-80">
            {draft[spec.opensField] && (
              <>Opens {formatMoment(fromLocalInput(draft[spec.opensField]))}. </>
            )}
            {draft[spec.closesField] && (
              <>Closes {formatMoment(fromLocalInput(draft[spec.closesField]))}.</>
            )}
          </p>
        )}
        {/* Saved vs typed, when they have drifted apart. */}
        {liveStatus !== preview.status.toLowerCase() && (
          <p className="mt-1.5 text-xs opacity-70">
            Currently saved and live: <b>{liveStatus}</b>. Save to apply what is typed above.
          </p>
        )}
      </Note>

      {children}
    </Panel>
  )
}

// ── The section ───────────────────────────────────────────────

export function SettingsSection() {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const settings = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => get<AdminSettings>('/api/admin/settings', 'staff'),
  })

  /*
   * The draft is seeded from the saved settings, and re-seeded only when one
   * of the saved *values* changes — not whenever the query object does.
   *
   * Depending on `settings.data` was wrong: TanStack Query hands back a new
   * object on every refetch, and the Admin console refetches off the dashboard
   * stream, which fires on every booth scan. Half-typed dates were being
   * wiped mid-keystroke during the entrance rush.
   */
  const savedSettings = settings.data?.settings
  const savedKey = savedSettings
    ? [
        savedSettings.mode,
        savedSettings.registrationOpensAt,
        savedSettings.registrationClosesAt,
        savedSettings.boothOpensAt,
        savedSettings.boothClosesAt,
      ].join('|')
    : null

  useEffect(() => {
    if (!savedSettings) return
    setDraft({
      mode: savedSettings.mode,
      registrationOpensAt: toLocalInput(savedSettings.registrationOpensAt),
      registrationClosesAt: toLocalInput(savedSettings.registrationClosesAt),
      boothOpensAt: toLocalInput(savedSettings.boothOpensAt),
      boothClosesAt: toLocalInput(savedSettings.boothClosesAt),
    })
    // Keyed on `savedKey`, the joined values — not on the query object, which
    // is new on every refetch. See the note above.
  }, [savedKey])

  /** Ticks once a second, on the server's clock — drives the live previews. */
  const nowMs = useServerNow(settings.data?.registration.serverNow)

  const save = useMutation({
    mutationFn: (body: Draft & { password: string }) =>
      api<AdminSettings>('/api/admin/settings', {
        method: 'PUT',
        auth: 'staff',
        body: JSON.stringify({
          mode: body.mode,
          registrationOpensAt: fromLocalInput(body.registrationOpensAt),
          registrationClosesAt: fromLocalInput(body.registrationClosesAt),
          boothOpensAt: fromLocalInput(body.boothOpensAt),
          boothClosesAt: fromLocalInput(body.boothClosesAt),
          password: body.password,
        }),
      }),
    onSuccess: (result) => {
      queryClient.setQueryData<AdminSettings>(['admin-settings'], (current) =>
        current ? { ...current, ...result } : result,
      )
      setDraft(draftFrom(result))
      setConfirming(false)
      setPassword('')
      setError(null)
      setSaved(true)

      /*
       * The booth scanners read their window from /api/booth/me. This console
       * may be the same browser a manager is testing in, so drop that cache
       * too rather than leaving it to the 30-second refetch.
       */
      void queryClient.invalidateQueries({ queryKey: ['booth'] })
      void queryClient.invalidateQueries({ queryKey: ['registration-window'] })
    },
    onError: (err) => {
      const failure = err as ApiError
      const detail = failure.detail as
        | { minutesLeft?: number; field?: string; window?: string }
        | undefined

      setError(
        failure.message === 'invalid_password'
          ? 'That is not your password. Nothing was changed.'
          : failure.message === 'locked'
            ? `Too many wrong attempts — your account is locked for ${detail?.minutesLeft ?? 15} minutes. Another admin can clear it from Accounts.`
            : failure.message === 'closes_before_opens'
              ? `The ${detail?.window === 'booth' ? 'booth scanning' : 'registration'} window closes before it opens.`
              : failure.message === 'invalid_date'
                ? 'One of those dates could not be read. Check them and try again.'
                : 'Could not save the settings. Try again.',
      )
    },
  })

  if (settings.isPending || !draft) {
    return (
      <Section title="Settings" description="Run mode, registration and booth scanning windows.">
        <div className="grid place-items-center gap-3 py-24">
          <span className="size-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-orange-500" />
          <p className="text-sm text-slate-500">Loading settings…</p>
        </div>
      </Section>
    )
  }

  if (settings.isError || !settings.data) {
    return (
      <Section title="Settings" description="Run mode, registration and booth scanning windows.">
        <Note tone="error">Could not load the settings. Refresh this section to try again.</Note>
      </Section>
    )
  }

  const current = settings.data
  const savedDraft = draftFrom(current)
  const changed = (Object.keys(savedDraft) as (keyof Draft)[]).some(
    (key) => draft[key] !== savedDraft[key],
  )

  /** Caught before the dialog, so no password is asked for a bad window. */
  const anyBackwards = WINDOWS.some((spec) => isBackwards(spec, draft))

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d))
    setSaved(false)
    setError(null)
  }

  const modeChanged = draft.mode !== current.settings.mode

  return (
    <Section
      title="Settings"
      description="Run mode, registration and booth scanning windows."
      actions={
        <>
          {changed && <Chip tone="attention">Unsaved changes</Chip>}
          {!changed && saved && <Chip tone="positive">Saved</Chip>}
          <AButton
            tone="outline"
            size="sm"
            disabled={!changed || save.isPending}
            onClick={() => {
              setDraft(savedDraft)
              setError(null)
            }}
          >
            <RotateCcw className="size-3.5" strokeWidth={2.2} />
            Discard
          </AButton>
          <AButton
            size="sm"
            disabled={!changed || anyBackwards || save.isPending}
            onClick={() => {
              setError(null)
              setPassword('')
              setConfirming(true)
            }}
          >
            <Lock className="size-3.5" strokeWidth={2.2} />
            Save changes
          </AButton>
        </>
      }
    >
      <div className="space-y-3">
        {error && !confirming && <Note tone="error">{error}</Note>}

        {/* ── Run mode ── */}
        <Panel
          title="Run mode"
          description="Which set of rules the app applies to everyone using it."
          actions={
            <Chip tone={current.settings.mode === 'production' ? 'positive' : 'info'}>
              Live: {current.settings.mode}
            </Chip>
          }
        >
          <div className="grid gap-2.5 sm:grid-cols-2">
            {MODES.map((mode) => {
              const Icon = mode.icon
              const active = draft.mode === mode.value
              return (
                <button
                  key={mode.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => set('mode', mode.value)}
                  className={cn(
                    'flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40',
                    active
                      ? 'border-[#1B2A6B] bg-[#1B2A6B]/5 shadow-sm'
                      : 'border-slate-200 bg-white hover:bg-slate-50',
                  )}
                >
                  <span
                    className={cn(
                      'grid size-9 shrink-0 place-items-center rounded-lg',
                      active ? 'bg-[#1B2A6B] text-amber-400' : 'bg-slate-100 text-slate-400',
                    )}
                  >
                    <Icon className="size-4" strokeWidth={2.2} />
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-bold text-[#0B1E3F]">
                      {mode.label}
                      {active && <CircleDot className="size-3.5 text-orange-500" strokeWidth={2.6} />}
                    </span>
                    <span className="mt-0.5 block text-xs leading-snug text-slate-500">
                      {mode.blurb}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>

          {/*
            What the toggle does *not* do. The mail driver is deliberately not
            on this switch: a click in a browser must never be able to start
            sending real mail from the live Office 365 account to real
            students. It is set in .env on the server and shown here read-only
            so the Committee can see the real state in one place.
          */}
          {current.environment && (
            <dl className="mt-3.5 grid gap-x-6 gap-y-1.5 rounded-xl bg-slate-50 px-3.5 py-3 text-xs sm:grid-cols-2">
              {[
                ['Mail driver', current.environment.mailDriver +
                  (current.environment.mailDriver === 'capture' ? ' · nothing is sent' : '')],
                ['Dev mailbox', current.environment.devMailbox ? 'reachable' : 'off'],
                ['NODE_ENV', current.environment.nodeEnv],
                ['Web origin', current.environment.webOrigin],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-3">
                  <dt className="font-bold uppercase tracking-[0.1em] text-slate-400">{label}</dt>
                  <dd className="truncate font-semibold text-slate-600">{value}</dd>
                </div>
              ))}
            </dl>
          )}
          <p className="mt-2 px-1 text-[11.5px] leading-snug text-slate-400">
            Those four are set on the server in <code>.env</code> and are not changed by this
            toggle — so switching to Production here cannot start sending real e-mail by itself.
          </p>
        </Panel>

        {/* ── Registration window ── */}
        <WindowPanel
          spec={WINDOWS[0]}
          draft={draft}
          nowMs={nowMs}
          liveStatus={
            current.registration.enforced
              ? LIVE_LABEL[current.registration.status]
              : 'always open'
          }
          onChange={set}
        >
          {/*
            The asymmetry is the thing to get across. An admin reading
            "window" will assume both ends behave the same way, and here they
            deliberately do not.
          */}
          <Note className="mt-3">
            <p className="font-semibold text-[#0B1E3F]">The two ends do different things.</p>
            <dl className="mt-1.5 space-y-1.5 text-[13px]">
              <div>
                <dt className="font-semibold text-[#0B1E3F]">Before it opens — nothing works.</dt>
                <dd className="text-slate-600">
                  Registering and signing in are both refused; students see the countdown. There is
                  no event to sign in to yet.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-[#0B1E3F]">
                  After it closes — only new sign-ups stop.
                </dt>
                <dd className="text-slate-600">
                  Students who registered can still sign in, open their QR pass, be scanned at
                  booths and collect their certificate. Closing the door on them mid-event would
                  strand anyone who cleared their browser or swapped phone.
                </dd>
              </div>
            </dl>
            <p className="mt-2 text-xs text-slate-400">
              A code already sent can always be entered, whichever side of the window it lands on.
            </p>
          </Note>
        </WindowPanel>

        {/* ── Booth scanning window ── */}
        <WindowPanel
          spec={WINDOWS[1]}
          draft={draft}
          nowMs={nowMs}
          liveStatus={current.booth.enforced ? LIVE_LABEL[current.booth.status] : 'always open'}
          onChange={set}
        >
          <Note className="mt-3">
            <p className="font-semibold text-[#0B1E3F]">Managers can always sign in.</p>
            <p className="mt-0.5 text-[13px]">
              Only the scan is refused — a manager outside the window reaches the scanner and sees
              the countdown or the closed notice, so nobody has to ask the help desk why their
              password stopped working. Scans already taken are kept and stay counted.
            </p>
            <p className="mt-1.5 text-xs text-slate-400">
              Judging, the lucky draws and the award ceremony are not affected by this window.
            </p>
          </Note>
        </WindowPanel>

        {current.settings.updatedAt && (
          <p className="px-1 text-xs text-slate-400">
            Last changed {formatMoment(current.settings.updatedAt)}
            {current.settings.updatedBy ? ` by ${current.settings.updatedBy}` : ''}.
          </p>
        )}
      </div>

      {/*
        The password gate.

        These settings decide whether ~1,250 students can register and whether
        all 14 booths can mark attendance, and the console sits signed in on a
        laptop at the Committee desk all day with people walking past it.
        Re-typing the password is what stops a mis-click stopping the floor.
      */}
      <Dialog
        open={confirming}
        onOpenChange={(open) => {
          if (save.isPending) return
          setConfirming(open)
          if (!open) setPassword('')
        }}
      >
        <DialogContent className="p-0" showClose={false}>
          <form
            className="px-6 pb-6 pt-7"
            onSubmit={(e) => {
              e.preventDefault()
              if (!password || save.isPending) return
              save.mutate({ ...draft, password })
            }}
          >
            <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-[#1B2A6B] text-white">
              <Lock className="size-5" strokeWidth={2.2} />
            </span>
            <DialogTitle className="text-center text-lg font-bold text-[#0B1E3F]">
              Confirm with your password
            </DialogTitle>
            <DialogDescription className="mt-1.5 text-center text-sm text-slate-500">
              {modeChanged ? (
                <>
                  Switching the run mode to <b>{draft.mode}</b>
                  {draft.mode === 'production'
                    ? ' will start applying both windows — to every student and every booth.'
                    : ' will stop both windows being applied.'}
                </>
              ) : (
                <>This changes when students may register and when booths may scan.</>
              )}
            </DialogDescription>

            <label
              htmlFor="settings-password"
              className="mt-5 mb-1 block text-[12.5px] font-bold text-[#0B1E3F]"
            >
              Your password
            </label>
            <input
              id="settings-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setError(null)
              }}
              aria-invalid={!!error}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 aria-[invalid=true]:border-red-300"
            />

            {error && <Note tone="error" className="mt-3">{error}</Note>}

            <div className="mt-5 flex gap-2.5">
              <AButton
                tone="outline"
                block
                type="button"
                disabled={save.isPending}
                onClick={() => {
                  setConfirming(false)
                  setPassword('')
                }}
              >
                Cancel
              </AButton>
              <AButton tone="navy" block type="submit" disabled={!password || save.isPending}>
                <Check className="size-4" strokeWidth={2.4} />
                {save.isPending ? 'Saving…' : 'Confirm & save'}
              </AButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Section>
  )
}
